import React, { useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Email, ArrowBack, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  decryptResponse,
  encryptResponse,
  validateEmail,
  validateField,
  validatePassword
} from '../../services';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import OtpInput from '../../components/common/OtpInput';

const ForgotPassword: React.FC = () => {
  const [step, setStep] = useState<'email' | 'otp' | 'password' | 'success'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailVerifyKey, setEmailVerifyKey] = useState('');
  const [errors, setErrors] = useState({
    email: '',
    otp: '',
    password: '',
    confirmPassword: ''
  });
  const [touched, setTouched] = useState({
    email: false,
    otp: false,
    password: false,
    confirmPassword: false
  });
  const navigate = useNavigate();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (touched.email) {
      const error = validateEmail(value);
      setErrors({
        ...errors,
        email: error
      });
    }
  };

  const handleOtpChange = (value: string) => {
    setOtp(value);

    if (touched.otp) {
      const error = value.length !== 6 ? 'OTP must be 6 digits' : '';
      setErrors({
        ...errors,
        otp: error
      });
    }
  };

  const handleOtpBlur = () => {
    setTouched({
      ...touched,
      otp: true
    });
    setErrors({
      ...errors,
      otp: otp.length !== 6 ? 'OTP must be 6 digits' : ''
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);

    if (touched.password) {
      const error = validatePassword(value);
      setErrors({
        ...errors,
        password: error
      });
    }

    // Also validate confirm password if it's already touched
    if (touched.confirmPassword && confirmPassword) {
      const confirmError = confirmPassword !== value ? 'Passwords do not match' : '';
      setErrors({
        ...errors,
        password: validatePassword(value),
        confirmPassword: confirmError
      });
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);

    if (touched.confirmPassword) {
      const error = value !== password ? 'Passwords do not match' : '';
      setErrors({
        ...errors,
        confirmPassword: error
      });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched({
      ...touched,
      [name]: true
    });

    let error = '';
    if (name === 'email') {
      error = validateEmail(value);
    } else if (name === 'otp') {
      error = value.length !== 6 ? 'OTP must be 6 digits' : '';
    } else if (name === 'password') {
      error = validatePassword(value);
    } else if (name === 'confirmPassword') {
      error = value !== password ? 'Passwords do not match' : '';
    }

    setErrors({
      ...errors,
      [name]: error
    });
  };

  // Step 1: Send OTP
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({
      ...touched,
      email: true
    });

    const emailError = validateEmail(email);
    setErrors({
      ...errors,
      email: emailError
    });

    if (emailError) {
      const element = document.getElementById('email');
      element?.focus();
      return;
    }

    setLoading(true);

    try {
      const sendOtpPayload = {
        request_id: "forgot_password_otp",
        action: "send_otp",
        data: await encryptResponse({
          email_id: email,
          purpose: "mfa"
        }, REQ_ENC_KEY)
      };

      type SendOtpResponse = { status: number; message?: string; [key: string]: any };
      const response = await httpService.post<SendOtpResponse>(
        `${API_BASE_URL}/user/sendOTP`,
        sendOtpPayload
      );

      if (response.data.status === 200) {
        setStep('otp');
        toast.success('OTP sent to your email!', {
          position: 'top-right',
          autoClose: 3000
        });
      } else {
        const errorMessage = response.data?.error?.errorDetails || 'Failed to send OTP. Please try again.';
        setErrors({
          ...errors,
          email: 'Email not found. Please check your email address.'
        });
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      setErrors({
        ...errors,
        email: 'An error occurred. Please try again later.'
      });
      toast.error('An error occurred. Please try again later.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({
      ...touched,
      otp: true
    });

    const otpError = otp.length !== 6 ? 'OTP must be 6 digits' : '';
    setErrors({
      ...errors,
      otp: otpError
    });

    if (otpError) {
      const element = document.getElementById('otp');
      element?.focus();
      return;
    }

    setLoading(true);

    try {
      const verifyOtpPayload = {
        request_id: "forgot_password_verify_otp",
        action: "verify_otp",
        data: await encryptResponse({
          email_id: email,
          otp: otp
        }, REQ_ENC_KEY)
      };

      type VerifyOtpResponse = { status: number; message?: string; data?: any; [key: string]: any };
      const response = await httpService.post<VerifyOtpResponse>(
        `${API_BASE_URL}/user/verifyOTP`,
        verifyOtpPayload
      );

      console.log("Verify OTP Response ::", response);

      if (response.data.status === 200 && response.data.data) {
        const decryptedData = await decryptResponse(response.data.data, REQ_ENC_KEY);
        const is_email_verified = (decryptedData as any).is_email_verified;
        setStep('password');
        toast.success('OTP verified successfully!', {
          position: 'top-right',
          autoClose: 3000
        });
        setEmailVerifyKey(is_email_verified);
      } else {
        setErrors({
          ...errors,
          otp: 'Invalid OTP. Please try again.'
        });
        toast.error('Invalid OTP. Please try again.', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      setErrors({
        ...errors,
        otp: 'An error occurred. Please try again later.'
      });
      toast.error('An error occurred. Please try again later.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({
      ...touched,
      password: true,
      confirmPassword: true
    });

    const passwordError = validatePassword(password);
    const confirmPasswordError = confirmPassword !== password ? 'Passwords do not match' : '';

    setErrors({
      ...errors,
      password: passwordError,
      confirmPassword: confirmPasswordError
    });

    if (passwordError || confirmPasswordError) {
      const firstErrorField = passwordError ? 'password' : 'confirmPassword';
      const element = document.getElementById(firstErrorField);
      element?.focus();
      return;
    }

    setLoading(true);

    try {
      const resetPasswordPayload = {
        request_id: "forgot_password_reset",
        action: "forgot_password",
        data: await encryptResponse({
          email_id: email,
          password: password,
          confirm_password: confirmPassword,
          is_email_verified: emailVerifyKey
        }, REQ_ENC_KEY)
      };

      type ResetPasswordResponse = { status: number; message?: string; [key: string]: any };
      const response = await httpService.post<ResetPasswordResponse>(
        `${API_BASE_URL}/user/forgotPassword`,
        resetPasswordPayload
      );

      console.log("Reset Password Response ::", response);

      if (response.data.status === 200 && response.data.data) {
        setStep('success');
        toast.success('Password reset successfully!', {
          position: 'top-right',
          autoClose: 3000
        });
      } else {
        toast.error('Failed to reset password. Please try again.', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      toast.error('An error occurred. Please try again later.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleBackToPreviousStep = () => {
    if (step === 'otp') {
      setStep('email');
      setOtp('');
      setErrors({ ...errors, otp: '' });
    } else if (step === 'password') {
      setStep('otp');
      setPassword('');
      setConfirmPassword('');
      setErrors({ ...errors, password: '', confirmPassword: '' });
    }
  };

  const togglePasswordVisibility = (field: 'password' | 'confirmPassword') => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  // Success Screen
  if (step === 'success') {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            position: 'absolute',
            top: 14,
            left: 24,
            zIndex: 1000,
          }}
        >
          <img
            src="/images/sampark-logo.png"
            alt="Company Logo"
            style={{ height: '80px', width: 'auto' }}
          />
        </Box>
        <Box
          sx={{
            marginTop: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography component="h1" variant="h4" gutterBottom>
                Password Reset Successfully
              </Typography>
              <Typography variant="body1" align="center" sx={{ mt: 2, mb: 3 }}>
                Your password has been reset successfully. You can now login with your new password.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={handleBackToLogin}
                sx={{ mt: 2, py: 1.5 }}
              >
                Continue to Login
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Main Form Screens
  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          position: 'absolute',
          top: 14,
          left: 24,
          zIndex: 1000,
        }}
      >
        <img
          src="/images/sampark-logo.png"
          alt="Company Logo"
          style={{ height: '80px', width: 'auto' }}
        />
      </Box>
      <Box
        sx={{
          marginTop: 20,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Step 1: Email */}
            {step === 'email' && (
              <>
                <Typography component="h1" variant="h4" gutterBottom>
                  Forgot Password
                </Typography>
                <Typography variant="body2" align="center" sx={{ mt: 1, mb: 3, color: 'text.secondary' }}>
                  Enter your email address and we'll send you an OTP to reset your password.
                </Typography>
                <Box component="form" onSubmit={handleSendOtp} sx={{ mt: 1, width: '100%' }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={
                      !email ||
                      Boolean(errors.email) ||
                      loading
                    }
                    sx={{ mt: 3, mb: 2, py: 1.5 }}
                  >
                    {loading ? 'Sending OTP...' : 'Send OTP'}
                  </Button>
                </Box>
              </>
            )}

            {/* Step 2: OTP */}
            {step === 'otp' && (
              <>
                <Typography component="h1" variant="h4" gutterBottom>
                  Verify OTP
                </Typography>
                <Typography variant="body2" align="center" sx={{ mt: 1, mb: 3, color: 'text.secondary' }}>
                  We've sent a 6-digit OTP to <strong>{email}</strong>. Enter it below to verify your identity.
                </Typography>
                <Box component="form" onSubmit={handleVerifyOtp} sx={{ mt: 1, width: '100%' }}>
                  <OtpInput
                    id="otp"
                    value={otp}
                    onChange={handleOtpChange}
                    onBlur={handleOtpBlur}
                    error={touched.otp && Boolean(errors.otp)}
                    helperText={touched.otp ? errors.otp : ''}
                    autoFocus
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={
                      otp.length !== 6 ||
                      Boolean(errors.otp) ||
                      loading
                    }
                    sx={{ mt: 3, mb: 2, py: 1.5 }}
                  >
                    {loading ? 'Verifying...' : 'Verify OTP'}
                  </Button>
                </Box>
              </>
            )}

            {/* Step 3: Reset Password */}
            {step === 'password' && (
              <>
                <Typography component="h1" variant="h4" gutterBottom>
                  Reset Password
                </Typography>
                <Typography variant="body2" align="center" sx={{ mt: 1, mb: 3, color: 'text.secondary' }}>
                  Enter your new password below.
                </Typography>
                <Box component="form" onSubmit={handleResetPassword} sx={{ mt: 1, width: '100%' }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="password"
                    label="New Password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    autoFocus
                    value={password}
                    onChange={handlePasswordChange}
                    onBlur={handleBlur}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => togglePasswordVisibility('password')}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="confirmPassword"
                    label="Confirm New Password"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    onBlur={handleBlur}
                    error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => togglePasswordVisibility('confirmPassword')}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={
                      !password ||
                      !confirmPassword ||
                      Boolean(errors.password) ||
                      Boolean(errors.confirmPassword) ||
                      loading
                    }
                    sx={{ mt: 3, mb: 2, py: 1.5 }}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </Box>
              </>
            )}

            {/* Navigation Links */}
            <Box sx={{ textAlign: 'center' }}>
              {step !== 'email' && (
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleBackToPreviousStep}
                  sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}
                >
                  <ArrowBack sx={{ mr: 1, fontSize: '16px' }} />
                  Back
                </Link>
              )}
              <Link
                component="button"
                variant="body2"
                onClick={handleBackToLogin}
                sx={{ textDecoration: 'none' }}
              >
                Back to Login
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
